import React from 'react';
import { Reservation, ReservationFieldDef, Table } from '../../interfaces';
import { LateStatus, RESERVATION_STATUS_META } from '../../hooks/useReservations';

interface Props {
    reservation: Reservation;
    /** Field definitions flagged showOnCard, already filtered + ordered. */
    cardFields: ReservationFieldDef[];
    tables: Table[];
    /** Minutes this party is likely to wait because its table is running over. */
    riskMinutes: number;
    /** Whether the party has been left standing, measured against the venue's grace. */
    late: LateStatus;
    /** Set when the booking's table is on a floor other than the one on screen. */
    otherFloor?: string;
    isActive: boolean;
    isAssigning: boolean;
    onSelect: (r: Reservation) => void;
    onAssign: (r: Reservation) => void;
    onSeat: (r: Reservation) => void;
    onEdit: (r: Reservation) => void;
    /** They rang ahead: hold the table rather than asking again. */
    onHold: (r: Reservation) => void;
    /** They never came. Frees the table for everybody else's numbers. */
    onNoShow: (r: Reservation) => void;
}

const icons = {
    people: <svg viewBox='0 0 24 24' width='12' height='12' fill='none'><path d='M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM22 19v-1a4 4 0 0 0-3-3.87M16 5.13A4 4 0 0 1 16 13' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' /></svg>,
    table: <svg viewBox='0 0 24 24' width='12' height='12' fill='none'><path d='M3 9h18M5 9v10M19 9v10M4 5h16l1 4H3l1-4Z' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' /></svg>,
    room: <svg viewBox='0 0 24 24' width='12' height='12' fill='none'><path d='M4 21V6l8-3 8 3v15M4 21h16M9 21v-6h6v6' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' /></svg>,
    note: <svg viewBox='0 0 24 24' width='12' height='12' fill='none'><path d='M8 6h8M8 10h8M8 14h5M5 3h14v18l-7-3-7 3V3Z' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' /></svg>,
    clock: <svg viewBox='0 0 24 24' width='12' height='12' fill='none'><circle cx='12' cy='12' r='9' stroke='currentColor' strokeWidth='1.8' /><path d='M12 7v5l3 2' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' /></svg>,
    star: <svg viewBox='0 0 24 24' width='11' height='11' fill='currentColor'><path d='M12 2.5l2.6 5.7 6.2.6-4.7 4.1 1.4 6.1L12 16l-5.5 3.1 1.4-6.1-4.7-4.1 6.2-.6L12 2.5Z' /></svg>,
};

const fmtWait = (m: number) => (m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`);

/** Renders a stored custom-field value in a way that reads well on one line. */
function formatValue(value: unknown): string {
    if (value == null || value === '' || value === false) return '';
    if (value === true) return 'Yes';
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
}

const ReservationCard: React.FC<Props> = ({
    reservation: r,
    cardFields,
    tables,
    riskMinutes,
    late,
    otherFloor,
    isActive,
    isAssigning,
    onSelect,
    onAssign,
    onSeat,
    onEdit,
    onHold,
    onNoShow,
}) => {
    const meta = RESERVATION_STATUS_META[r.status];
    const tableNames = r.tableIds
        .map(id => tables.find(t => t.id === id)?.tableNo)
        .filter(Boolean)
        .join(' + ');
    const guestName = `${r.guest?.firstName || 'Guest'}${r.guest?.lastName ? ` ${r.guest.lastName}` : ''}`;
    const isClosed = r.status === 'completed' || r.status === 'cancelled' || r.status === 'noShow';
    const canSeat = r.status === 'confirmed' || r.status === 'arrived';

    const chips = cardFields
        .map(f => ({ label: f.label, value: formatValue(r.customFields[f.key]), type: f.type }))
        .filter(c => c.value);

    const classes = [
        'resv-card',
        isActive ? 'is-active' : '',
        isAssigning ? 'is-assigning' : '',
        isClosed ? 'is-closed' : '',
        late.late ? 'is-late' : '',
    ].filter(Boolean).join(' ');

    return (
        <div
            className={classes}
            style={{ ['--accent' as any]: meta.accent }}
            role='button'
            tabIndex={0}
            onClick={() => onSelect(r)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(r); } }}
        >
            <span className='resv-card__bar' />

            <div className='resv-card__top'>
                <span className='resv-card__time'>{r.time}</span>
                <span className='resv-card__status'>{meta.label}</span>
            </div>

            <div className='resv-card__name'>
                {guestName}
                {r.guest.isVip && <i className='resv-card__vip' title='VIP'>{icons.star}</i>}
                {r.guest.isRepeat && !r.guest.isVip && <i className='resv-card__repeat' title='Repeat guest'>Regular</i>}
            </div>

            <div className='resv-card__metas'>
                <span className='resv-card__meta'>{icons.people}{r.partySize}</span>
                {tableNames ? (
                    <span className='resv-card__meta'>
                        {icons.table}{tableNames}
                        {/* Which room, when it is not this one: T4 on the Terrace is not
                          * the T4 on the board in front of the host. */}
                        {otherFloor && <span className='resv-card__floor'>{otherFloor}</span>}
                    </span>
                ) : (
                    <span className='resv-card__meta resv-card__meta--warn'>{icons.table}No table</span>
                )}
                {r.prostay?.roomNumber && (
                    <span className='resv-card__meta'>{icons.room}Rm {r.prostay.roomNumber}</span>
                )}
            </div>

            {chips.length > 0 && (
                <div className='resv-card__chips'>
                    {chips.map(c => (
                        <span key={c.label} className='resv-card__chip' title={`${c.label}: ${c.value}`}>
                            {c.type === 'checkbox' ? c.label : c.value}
                        </span>
                    ))}
                </div>
            )}

            {/* The one thing on this card the floor can settle without opening it, so
              * the question and both answers sit on its face. Until one of them is
              * tapped the table stays held, because silence must not give it away. */}
            {late.late && (
                <div className='resv-card__late'>
                    {icons.clock}
                    <span className='resv-card__late-msg'>
                        {fmtWait(late.lateBy)} late{tableNames ? ` · ${tableNames} held` : ''}
                    </span>
                    <span className='resv-card__late-acts' onClick={(e) => e.stopPropagation()}>
                        <button
                            type='button'
                            className='resv-btn resv-btn--ghost resv-btn--tight'
                            onClick={() => onHold(r)}
                            title='They rang ahead — keep the table and stop asking'
                        >Still coming</button>
                        <button
                            type='button'
                            className='resv-btn resv-btn--danger resv-btn--tight'
                            onClick={() => onNoShow(r)}
                            title={`They never arrived — free ${tableNames || 'the table'} for the queue`}
                        >No show</button>
                    </span>
                </div>
            )}

            {late.held && (
                <div className='resv-card__held'>
                    {icons.clock}
                    <span>On their way — held {fmtWait(late.heldFor)} more</span>
                </div>
            )}

            {riskMinutes > 0 && !isClosed && (
                <div
                    className='resv-card__risk'
                    title={`${tableNames || 'The table'} is past its turn time, so this party may not be seated until about ${fmtWait(riskMinutes)} after ${r.time}.`}
                >
                    {icons.clock}
                    <span>Table running over — seating ~{fmtWait(riskMinutes)} late</span>
                </div>
            )}

            {(r.guestNotes || r.internalNotes) && (
                <div className='resv-card__note'>
                    {icons.note}
                    <span>{r.guestNotes || r.internalNotes}</span>
                </div>
            )}

            {isActive && !isClosed && (
                <div className='resv-card__actions' onClick={(e) => e.stopPropagation()}>
                    {r.status !== 'seated' && (
                        <button type='button' className='resv-btn resv-btn--ghost' onClick={() => onAssign(r)}>
                            {r.tableIds.length > 0 ? 'Change table' : 'Assign table'}
                        </button>
                    )}
                    {canSeat && (
                        <button type='button' className='resv-btn resv-btn--primary' onClick={() => onSeat(r)}>
                            Seat
                        </button>
                    )}
                    <button type='button' className='resv-btn resv-btn--ghost' onClick={() => onEdit(r)}>
                        Details
                    </button>
                </div>
            )}
        </div>
    );
};

export default ReservationCard;
